import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  styled,
  CircularProgress,
} from '@mui/material';
import { CloudArrowUp, X } from '@phosphor-icons/react';
import theme from '../../theme';
import ReusableModal from '../common/Modal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadKnowledgeBaseFileApi } from '../../utils/apis';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';
import { useUser } from '../../hooks/useUser';

const UploadIconBox = styled(Box)({
  backgroundColor: 'custom.sidebarIconBg',
  padding: '8px',
  borderRadius: '6px',
  marginBottom: '8px',
});

const UploadInstructions = styled(Typography)({
  textAlign: 'center',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  color: 'text.secondary',
});

const UploadButton = styled(Button)({
  color: 'primary.main',
  textTransform: 'none',
  padding: 0,
  minWidth: 'auto',
});

interface Props {
  open: boolean;
  onClose: () => void;
  selectedAssistant?: any;
  selectedStore?: any;
}

const KnowledgeBaseFileUploadModal = ({
  open,
  onClose,
  selectedAssistant,
  selectedStore,
}: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { alert, setAlert, showAlert } = useAlert();
  const { user } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  const handleFileUpload = (event: React.MouseEvent) => {
    event.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = [
        'text/plain',
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];

      if (!allowedTypes.includes(file.type)) {
        showAlert(
          'Invalid file type. Only Text, DOCX, or PDF files are allowed.',
          'error'
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setSelectedFile(file);
    }
  };

  const { mutate: uploadFile, isPending } = useMutation({
    mutationFn: uploadKnowledgeBaseFileApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['stores'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-store-knowledge-files'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-knowledge-base-files'],
      });
      setSelectedFile(null);
      showAlert('File uploaded successfully!', 'success');
      onClose();
    },
    onError: () => {
      showAlert('Failed to upload file. Please try again.', 'error');
      queryClient.invalidateQueries({
        queryKey: ['get-store-knowledge-files'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-knowledge-base-files'],
      });
      setSelectedFile(null);
      onClose();
    },
  });

  const handleSubmitFile = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (selectedFile) {
      uploadFile({
        userId: user._id,
        storeId: selectedStore.id,
        file: selectedFile,
      });
    }
  };

  const handleRemoveFile = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box>
      <ReusableModal
        open={open}
        onClose={onClose}
        title={`Knowledge Base for ${selectedAssistant?.agentName || 'Agent'}`}
        subtitle="Please upload a file for knowledge base"
        width={500}
      >
        <Box sx={{ mt: 2 }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".txt,.docx,.pdf"
            style={{ display: 'none' }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed #DCE0E7',
              borderRadius: '8px',
              p: 3,
              width: '100%',
              cursor: 'pointer',
            }}
            onClick={handleFileUpload}
          >
            {!selectedFile ? (
              <>
                <UploadIconBox>
                  <CloudArrowUp
                    size={24}
                    color={theme.palette.lightGray.text}
                  />
                </UploadIconBox>
                <UploadInstructions variant="body2">
                  Only <strong>Text or PDF</strong> files are supported. <br />
                  Please upload a valid file.
                  <br />
                  (Max file size: 5MB)
                </UploadInstructions>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}
                >
                  <UploadInstructions variant="body2">
                    Drop files here or
                  </UploadInstructions>
                  <UploadButton onClick={handleFileUpload}>
                    Browse from device
                  </UploadButton>
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    width: '100%',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body1">
                    Selected file: {selectedFile.name}
                  </Typography>
                  <Box
                    onClick={handleRemoveFile}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        opacity: 0.7,
                      },
                    }}
                  >
                    <X size={20} color={theme.palette.error.main} />
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleSubmitFile}
                  disabled={isPending}
                  sx={{ textTransform: 'none' }}
                  startIcon={
                    isPending ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {isPending ? 'Uploading...' : 'Upload File'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </ReusableModal>
      {alert && <MessagePopup alert={alert} setAlert={setAlert} />}
    </Box>
  );
};

export default KnowledgeBaseFileUploadModal;
